/**
 * The timber tally sheet.
 *
 * WHY THIS EXISTS
 * ---------------
 * Wooden runners are stocked in CFT and the auditors have approved that. No
 * vendor in Tripura sells timber by the cubic foot: the challan reads "40 NO,
 * 12 ft" and, until now, the store-man had to convert it in his head at the
 * gate with a lorry waiting.
 *
 *     CFT = pieces x (width" x thickness" / 144) x length in feet
 *
 * 144 is square inches to the square foot. A 3" x 4" runner 12 ft long is
 * exactly 1.000 CFT, which is the figure any timber merchant will quote back
 * at you — a useful thing for a store-man to recognise on screen.
 *
 * WHY A SHEET AND NOT A CALCULATOR
 * --------------------------------
 * A lorry does not bring one length. It brings 15 pieces at 12 ft and 25 at
 * 10 ft, and the challan lists them exactly that way, footed to a volume. This
 * is a TALLY SHEET, which is what the trade has always used, so an auditor can
 * hold the paper challan beside the screen and read the same rows in the same
 * order.
 *
 * WHERE THE NUMBER GOES
 * ---------------------
 * The footed total IS the line quantity. The parent screen stores the rows and
 * sets its quantity from `tallyCft(rows)` — it never asks the store-man to
 * type a volume, because a volume typed by hand is a volume nobody can check.
 *
 * The same arithmetic is enforced in the database by fn_timber_tally_guard,
 * which recomputes every row and refuses a line whose sheet does not foot to
 * its quantity. What follows is the courtesy; that is the control.
 */
import { Plus, Trash2 } from 'lucide-react'
import type { Material } from '@/lib/types'

export interface TallyRow {
  key: string
  pieces: string
  width_in: string
  thickness_in: string
  length_ft: string
}

/** What is written to the database: the same rows, with each volume worked out. */
export interface TallyJson {
  pieces: number
  width_in: number
  thickness_in: number
  length_ft: number
  cft: number
}

/**
 * Is this material measured rather than counted?
 *
 * Both dimensions present, and nothing else. There is deliberately no separate
 * flag that could fall out of step with them.
 */
export const isTimber = (m?: Material | null): boolean =>
  !!m && m.cft_width_in != null && m.cft_thickness_in != null
    && Number(m.cft_width_in) > 0 && Number(m.cft_thickness_in) > 0

const n = (s: string) => {
  const v = Number(String(s).trim())
  return Number.isFinite(v) ? v : 0
}

/** One row's volume, rounded to three decimals — the figure that prints. */
export const rowCft = (r: TallyRow): number =>
  Math.round(n(r.pieces) * n(r.width_in) * n(r.thickness_in) * n(r.length_ft) / 144 * 1000) / 1000

/**
 * The footed total.
 *
 * EACH ROW IS ROUNDED, THEN THE ROWS ARE ADDED. Rounding the sum instead would
 * give a foot that does not equal its own column: forty rows of 3x4x7ft add to
 * 23.320 row by row and 23.333 in one go, and the database — which also works
 * row by row — would then refuse the line as not tying. fn_timber_cft in the
 * migration rounds the same way, on purpose.
 */
export const tallyCft = (rows: TallyRow[]): number =>
  Math.round(rows.reduce((a, r) => a + rowCft(r), 0) * 1000) / 1000

export const totalPieces = (rows: TallyRow[]): number =>
  rows.reduce((a, r) => a + n(r.pieces), 0)

/** A blank row, pre-filled with the material's nominal cross-section. */
export const blankTallyRow = (m?: Material | null): TallyRow => ({
  key: crypto.randomUUID(),
  pieces: '',
  width_in: m?.cft_width_in != null ? String(m.cft_width_in) : '',
  thickness_in: m?.cft_thickness_in != null ? String(m.cft_thickness_in) : '',
  length_ft: '',
})

/** Rows -> what is stored on the line. Blank rows are dropped, never sent. */
export const tallyToJson = (rows: TallyRow[]): TallyJson[] | null => {
  const out = rows
    .filter((r) => n(r.pieces) > 0 && n(r.width_in) > 0 && n(r.thickness_in) > 0 && n(r.length_ft) > 0)
    .map((r) => ({
      pieces: n(r.pieces), width_in: n(r.width_in),
      thickness_in: n(r.thickness_in), length_ft: n(r.length_ft), cft: rowCft(r),
    }))
  return out.length ? out : null
}

/** What is stored on the line -> rows, for reopening a returned document. */
export const tallyFromJson = (json: unknown): TallyRow[] => {
  if (!Array.isArray(json)) return []
  return json.map((e: any) => ({
    key: crypto.randomUUID(),
    pieces: String(e?.pieces ?? ''),
    width_in: String(e?.width_in ?? ''),
    thickness_in: String(e?.thickness_in ?? ''),
    length_ft: String(e?.length_ft ?? ''),
  }))
}

export function TimberTally({ material, rows, onChange, disabled }: {
  material: Material
  rows: TallyRow[]
  onChange: (rows: TallyRow[]) => void
  disabled?: boolean
}) {
  const set = (key: string, patch: Partial<TallyRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const cft = tallyCft(rows)
  const pcs = totalPieces(rows)

  return (
    <div className="rounded-lg border border-amber-200 border-l-4 border-l-amber-600 bg-amber-50/50 p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">
          Timber tally — measured, not counted
        </span>
        <button type="button" className="btn-secondary btn-sm" disabled={disabled}
                onClick={() => onChange([blankTallyRow(material), ...rows])}>
          <Plus className="h-3.5 w-3.5" /> Add a length
        </button>
      </div>

      <p className="mb-2 text-[11px] text-amber-800">
        The challan is in pieces; the store is in {material.unit_of_measure}. Enter what you counted and
        the volume is worked out for you — one row per length on the lorry.
        Cross-section is pre-filled from the material and can be corrected if the sawn size differs.
      </p>

      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th className="num">Pieces (NO)</th>
              <th className="num">Width (in)</th>
              <th className="num">Thickness (in)</th>
              <th className="num">Length (ft)</th>
              <th className="num">CFT</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="num">
                  <input type="number" min="0" step="any" inputMode="decimal" disabled={disabled}
                         className="field w-24 text-right" value={r.pieces}
                         onChange={(e) => set(r.key, { pieces: e.target.value })} />
                </td>
                <td className="num">
                  <input type="number" min="0" step="any" inputMode="decimal" disabled={disabled}
                         className="field w-20 text-right" value={r.width_in}
                         onChange={(e) => set(r.key, { width_in: e.target.value })} />
                </td>
                <td className="num">
                  <input type="number" min="0" step="any" inputMode="decimal" disabled={disabled}
                         className="field w-20 text-right" value={r.thickness_in}
                         onChange={(e) => set(r.key, { thickness_in: e.target.value })} />
                </td>
                <td className="num">
                  <input type="number" min="0" step="any" inputMode="decimal" disabled={disabled}
                         className="field w-24 text-right" value={r.length_ft}
                         onChange={(e) => set(r.key, { length_ft: e.target.value })} />
                </td>
                <td className="num text-sm font-semibold text-slate-800">
                  {rowCft(r).toFixed(3)}
                </td>
                <td className="text-right">
                  {rows.length > 1 && (
                    <button type="button" className="btn-ghost btn-sm text-red-600" disabled={disabled}
                            title="Remove this length"
                            onClick={() => onChange(rows.filter((x) => x.key !== r.key))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-amber-300 bg-amber-100/60">
              <td className="num text-sm font-bold">{pcs || 0} NO</td>
              <td colSpan={3} className="text-right text-xs font-semibold uppercase tracking-wide text-amber-900">
                Total for this line
              </td>
              <td className="num text-sm font-bold text-amber-900">
                {cft.toFixed(3)} {material.unit_of_measure}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
